import { useState } from 'react';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { format, startOfDay, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "Reconstrucción de Transmisión",
  "Diagnóstico de Motor",
  "Aire Acondicionado (A/C)",
  "Frenos",
  "Afinación (Tune Up)",
  "Motor Diesel",
  "Motor a Gasolina",
  "Mantenimiento General",
  "Otro",
];

const timeSlots = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
];

const AppointmentSection = () => {
  const [date, setDate] = useState(undefined);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    time: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const appointmentData = {
        ...formData,
        date: date ? format(date, 'yyyy-MM-dd') : '',
      };

      await axios.post(`${API}/appointments`, appointmentData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        time: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_year: '',
        notes: '',
      });
      setDate(undefined);
    } catch (error) {
      console.error('Error submitting appointment:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && 
                      formData.service && formData.time && date &&
                      formData.vehicle_make && formData.vehicle_model && formData.vehicle_year;

  return (
    <section 
      id="appointments" 
      className="py-20 md:py-32 bg-zinc-950"
      data-testid="appointments-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span 
            className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400 mb-4 block"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Reserve Su Cita
          </span>
          <h2 
            className="text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="appointments-title"
          >
            Agende Una Cita Online
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Complete el formulario a continuación para programar su cita. 
            Nos pondremos en contacto para confirmar su reserva.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Calendar */}
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <h3 
              className="text-xl font-bold uppercase text-white mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Seleccione Una Fecha
            </h3>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              locale={es}
              disabled={(dateToCheck) => {
                const today = startOfDay(new Date());
                return isBefore(dateToCheck, today) || dateToCheck.getDay() === 0;
              }}
              className="bg-zinc-900 text-white"
              data-testid="appointment-calendar"
            />
            {date && (
              <div className="mt-4 p-4 bg-zinc-950 border border-zinc-800">
                <p className="text-zinc-400 text-sm">Fecha seleccionada:</p>
                <p className="text-white font-bold" data-testid="selected-date">
                  {format(date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
                </p>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="appointment-form">
            {/* Personal Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Nombre Completo *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="Juan Pérez"
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="juan@email.com"
                  data-testid="input-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                placeholder="+1 (555) 123-4567"
                data-testid="input-phone"
              />
            </div>

            {/* Service & Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Servicio *
                </label>
                <Select onValueChange={(value) => handleSelectChange('service', value)} value={formData.service}>
                  <SelectTrigger className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 h-auto focus:ring-2 focus:ring-blue-600" data-testid="select-service">
                    <SelectValue placeholder="Seleccione servicio" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {services.map((service) => (
                      <SelectItem key={service} value={service} className="text-zinc-100 focus:bg-zinc-800 focus:text-white">
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Hora *
                </label>
                <Select onValueChange={(value) => handleSelectChange('time', value)} value={formData.time}>
                  <SelectTrigger className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 h-auto focus:ring-2 focus:ring-blue-600" data-testid="select-time">
                    <SelectValue placeholder="Seleccione hora" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time} className="text-zinc-100 focus:bg-zinc-800 focus:text-white">
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Marca *
                </label>
                <input
                  type="text"
                  name="vehicle_make"
                  value={formData.vehicle_make}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="Toyota"
                  data-testid="input-vehicle-make"
                />
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Modelo *
                </label>
                <input
                  type="text"
                  name="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="Camry"
                  data-testid="input-vehicle-model"
                />
              </div>
              <div>
                <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                  Año *
                </label>
                <input
                  type="text"
                  name="vehicle_year"
                  value={formData.vehicle_year}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder="2020"
                  data-testid="input-vehicle-year"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-zinc-400 mb-2">
                Notas Adicionales
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 p-4 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none"
                placeholder="Describa brevemente el problema o servicio que necesita..."
                data-testid="input-notes"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`w-full font-bold uppercase tracking-widest px-8 py-4 transition-all flex items-center justify-center gap-2 ${
                isFormValid && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
              }`}
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="submit-appointment"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Enviando...
                </>
              ) : (
                'Confirmar Cita'
              )}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="flex items-center gap-3 p-4 bg-green-900/30 border border-green-700 text-green-400" data-testid="success-message">
                <CheckCircle size={20} />
                <span>¡Cita agendada exitosamente! Nos pondremos en contacto pronto.</span>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="flex items-center gap-3 p-4 bg-red-900/30 border border-red-700 text-red-400" data-testid="error-message">
                <AlertCircle size={20} />
                <span>Error al agendar la cita. Por favor intente nuevamente.</span>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default AppointmentSection;
